// Great-circle navigation utilities for real-world flight simulation

const EARTH_RADIUS_NM = 3440.065; // Earth radius in nautical miles

/**
 * Convert degrees to radians
 */
export function toRadians(degrees) {
  return degrees * Math.PI / 180;
}

/**
 * Convert radians to degrees
 */
export function toDegrees(radians) {
  return radians * 180 / Math.PI;
}

/**
 * Calculate great-circle distance between two points using Haversine formula
 * @param {number} lat1 - Latitude of point 1 in degrees
 * @param {number} lon1 - Longitude of point 1 in degrees
 * @param {number} lat2 - Latitude of point 2 in degrees
 * @param {number} lon2 - Longitude of point 2 in degrees
 * @returns {number} Distance in nautical miles
 */
export function haversineDistance(lat1, lon1, lat2, lon2) {
  const φ1 = toRadians(lat1);
  const φ2 = toRadians(lat2);
  const Δφ = toRadians(lat2 - lat1);
  const Δλ = toRadians(lon2 - lon1);

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
  return EARTH_RADIUS_NM * c;
}

/**
 * Calculate initial bearing from point 1 to point 2
 * @param {number} lat1 - Latitude of point 1 in degrees
 * @param {number} lon1 - Longitude of point 1 in degrees
 * @param {number} lat2 - Latitude of point 2 in degrees
 * @param {number} lon2 - Longitude of point 2 in degrees
 * @returns {number} Bearing in degrees (0-360)
 */
export function initialBearing(lat1, lon1, lat2, lon2) {
  const φ1 = toRadians(lat1);
  const φ2 = toRadians(lat2);
  const Δλ = toRadians(lon2 - lon1);

  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) -
            Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  
  const θ = Math.atan2(y, x);
  
  return (toDegrees(θ) + 360) % 360;
}

/**
 * Calculate intermediate point along great-circle path
 * @param {number} lat1 - Latitude of point 1 in degrees
 * @param {number} lon1 - Longitude of point 1 in degrees
 * @param {number} lat2 - Latitude of point 2 in degrees
 * @param {number} lon2 - Longitude of point 2 in degrees
 * @param {number} fraction - Fraction along path (0-1)
 * @returns {{lat: number, lon: number}} Intermediate point
 */
export function intermediatePoint(lat1, lon1, lat2, lon2, fraction) {
  if (fraction <= 0) return { lat: lat1, lon: lon1 };
  if (fraction >= 1) return { lat: lat2, lon: lon2 };

  const φ1 = toRadians(lat1);
  const λ1 = toRadians(lon1);
  const φ2 = toRadians(lat2);
  const λ2 = toRadians(lon2);

  const Δφ = φ2 - φ1;
  const Δλ = λ2 - λ1;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const δ = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  // CRASH FIX: If δ is 0 or near-zero (same origin/destination), avoid division by zero
  // This prevents NaN coordinates that crash Mapbox rendering
  if (δ < 0.0000001) {
    // Points are essentially the same - interpolate linearly to avoid NaN
    return {
      lat: lat1 + (lat2 - lat1) * fraction,
      lon: lon1 + (lon2 - lon1) * fraction
    };
  }

  const A = Math.sin((1 - fraction) * δ) / Math.sin(δ);
  const B = Math.sin(fraction * δ) / Math.sin(δ);

  const x = A * Math.cos(φ1) * Math.cos(λ1) + B * Math.cos(φ2) * Math.cos(λ2);
  const y = A * Math.cos(φ1) * Math.sin(λ1) + B * Math.cos(φ2) * Math.sin(λ2);
  const z = A * Math.sin(φ1) + B * Math.sin(φ2);

  const φ3 = Math.atan2(z, Math.sqrt(x * x + y * y));
  const λ3 = Math.atan2(y, x);

  return {
    lat: toDegrees(φ3),
    lon: toDegrees(λ3)
  };
}

/**
 * Calculate altitude for cruise phase based on progress
 * @param {number} progress - Flight progress (0-1)
 * @param {number} cruiseAltitude - Target cruise altitude in feet
 * @returns {number} Current altitude in feet
 */
export function calculateAltitude(progress, cruiseAltitude = 37000) {
  if (progress < 0.1) {
    // Climb phase (0-10% of flight)
    return Math.floor((progress / 0.1) * cruiseAltitude);
  } else if (progress > 0.9) {
    // Descent phase (90-100% of flight)
    const descentProgress = (progress - 0.9) / 0.1;
    return Math.floor(cruiseAltitude * (1 - descentProgress));
  } else {
    // Cruise phase
    return cruiseAltitude;
  }
}

/**
 * Calculate speed based on altitude
 * @param {number} altitude - Current altitude in feet
 * @param {number} cruiseSpeed - Cruise speed in knots
 * @returns {number} Current speed in knots
 */
export function calculateSpeed(altitude, cruiseSpeed = 449) {
  if (altitude < 1000) {
    return Math.floor((altitude / 1000) * 250); // Accelerating/decelerating
  } else if (altitude < 10000) {
    return 250; // Speed limit below 10,000 ft
  } else {
    const climbProgress = Math.min(1, (altitude - 10000) / 27000);
    return Math.floor(250 + climbProgress * (cruiseSpeed - 250));
  }
}